// Property Data Model v2 (integration-ready).
//
// This module defines the canonical shape of a SIL/SDA property record so that
// (1) internal components render off a stable contract,
// (2) a future AI agent / CRM / Jotform / Canva / CMS can create + update
//     records without knowing the internals of every React component,
// (3) new fields can be added without breaking existing pages.
//
// A property record is a plain object. The `normaliseProperty()` function
// merges an incoming record with sensible defaults so pages don't crash on
// records that were authored before a field existed.
//
// SECURITY NOTE: fields under `internal` MUST never be rendered publicly.

/** @typedef {"available"|"limited"|"upcoming"|"filled"|"eoi"|"paused"|"archived"} ListingStatus */

/**
 * Canonical (empty) shape — reference only. Do not import for defaults; use
 * normaliseProperty(input) instead.
 */
export const PROPERTY_SHAPE = Object.freeze({
  // BASIC INFORMATION
  id: "",                    // stable unique id (uuid or slug-based)
  slug: "",
  href: "",
  name: "",
  short: "",                 // short display label used in listing cards
  headline: "",
  oneLiner: "",

  // Publishing controls
  published: true,           // false = hidden from public search
  listing_status: "available", // See ListingStatus

  // VERIFICATION — critical production safeguard.
  //   "verified"           — a real Alpha or partner property, safe for production.
  //   "awaitingVerification"— submitted by partner but not yet confirmed.
  //   "demonstration"      — preview-only seed data; NEVER surfaced in production.
  recordVerificationStatus: "verified",

  // LOCATION
  suburb: "",
  city: "",                  // city slug matching data/nationalArchitecture.js
  state: "",                 // "VIC", "WA", ...
  postcode: "",
  region: "",                // e.g. "Inner north Melbourne"
  country: "AU",
  lat: null,
  lon: null,

  // VACANCY
  vacancy_available: true,
  vacancies: 0,              // number of vacant participant bedrooms
  total_bedrooms: 0,
  participant_bedrooms: 0,
  occupied_bedrooms: 0,
  ooa_room: false,           // has an Overnight On-call/Assistance room
  gender_preference: null,   // only set when legally required for compatibility
  preferred_participant_profile: "",
  available_from: null,      // ISO date string
  vacancy_last_updated: null, // ISO date — surfaces "vacancy freshness"

  // SIL SUPPORT MODEL
  support_ratios: [],        // ["1:1","1:2","2:1"]
  support_model: [],         // ["24-7","sleepover","active-overnight","drop-in","individualised","shared"]

  // ACCESSIBILITY
  sda_category: null,        // "improved-liveability" | "fully-accessible" | "high-physical-support" | "robust" | null
  accessibility_features: [], // canonical tag list (see ACCESSIBILITY_TAGS)

  // SUITABILITY (for referral discovery — NOT clinical eligibility)
  suitability_tags: [],      // see SUITABILITY_TAGS

  // PROPERTY FEATURES
  property_type: "house",    // "house"|"townhouse"|"apartment"|"unit"|"villa"
  bathrooms: 0,
  living_areas: 0,
  outdoor_area: false,
  secure_yard: false,
  parking: 0,
  garage: false,
  nearby: {
    transport: "",
    shops: "",
    medical: "",
    community: "",
  },

  // NARRATIVE (existing free-form content still supported)
  badges: [],
  specs: [],
  features: [],
  ndis: { summary: "", points: [] },
  description: [],
  quickFacts: [],
  images: [],
  story: {},
  accessibility: [],       // legacy rich chips (kept for existing rendering)
  idealFor: [],
  whyLove: [],
  lifestyle: {},

  // MEDIA
  hero_image: "",           // fallback: images[0]
  gallery: [],              // fallback: images
  floor_plan_url: null,
  video_url: null,
  virtual_tour_url: null,
  brochure_url: null,

  // SEO
  seo: {
    title: "",              // optional per-property SEO title override
    description: "",
    keywords: [],
    og_image: null,
  },

  // INTERNAL — never render publicly
  internal: {
    sda_provider: "",
    property_owner: "",
    referral_source: "",
    landlord: "",
    management_notes: "",
    contact: { name: "", email: "", phone: "" },
  },
});

// -----------------------------------------------------------------------------
// Canonical tag vocabularies — the shared vocabulary that filters, forms and
// AI agents will use. Keep values in kebab-case.
// -----------------------------------------------------------------------------

export const SUPPORT_RATIOS = [
  { value: "2:1", label: "2:1 (complex / high-intensity)" },
  { value: "1:1", label: "1:1" },
  { value: "1:2", label: "1:2" },
];

export const SUPPORT_MODELS = [
  { value: "24-7", label: "24/7 staffing" },
  { value: "sleepover", label: "Sleepover" },
  { value: "active-overnight", label: "Active overnight" },
  { value: "drop-in", label: "Drop-in" },
  { value: "individualised", label: "Individualised" },
  { value: "shared", label: "Shared" },
];

export const SDA_CATEGORIES = [
  { value: "improved-liveability", label: "Improved Liveability" },
  { value: "fully-accessible", label: "Fully Accessible" },
  { value: "high-physical-support", label: "High Physical Support" },
  { value: "robust", label: "Robust" },
];

export const ACCESSIBILITY_TAGS = [
  { value: "step-free-entry", label: "Step-free entry" },
  { value: "wheelchair-accessible", label: "Wheelchair accessible" },
  { value: "accessible-bathroom", label: "Accessible bathroom" },
  { value: "accessible-kitchen", label: "Accessible kitchen" },
  { value: "wide-doorways", label: "Wide doorways" },
  { value: "ceiling-hoist", label: "Ceiling hoist" },
  { value: "portable-hoist", label: "Portable hoist" },
  { value: "automated-doors", label: "Automated doors" },
  { value: "accessible-parking", label: "Accessible parking" },
  { value: "single-storey", label: "Single-storey" },
  { value: "lift-access", label: "Lift access" },
  { value: "sensory-friendly", label: "Sensory-friendly" },
  { value: "secure-outdoor", label: "Secure outdoor area" },
  { value: "reinforced-construction", label: "Reinforced construction" },
];

export const SUITABILITY_TAGS = [
  { value: "physical-disability", label: "Physical disability" },
  { value: "psychosocial-disability", label: "Psychosocial disability" },
  { value: "intellectual-disability", label: "Intellectual disability" },
  { value: "acquired-brain-injury", label: "Acquired brain injury" },
  { value: "neurological", label: "Neurological disability" },
  { value: "complex-health", label: "Complex health needs" },
  { value: "behaviours-of-concern", label: "Behaviours of concern" },
  { value: "forensic", label: "Forensic support" },
  { value: "high-intensity", label: "High intensity support" },
  { value: "community-transition", label: "Community transition" },
  { value: "hospital-discharge", label: "Hospital discharge" },
  { value: "justice-transition", label: "Justice transition" },
];

export const LISTING_STATUSES = [
  { value: "available", label: "Available now", tone: "green" },
  { value: "limited", label: "Limited vacancy", tone: "amber" },
  { value: "upcoming", label: "Coming soon", tone: "purple" },
  { value: "filled", label: "Fully occupied", tone: "neutral" },
  { value: "eoi", label: "Expressions of interest", tone: "purple" },
  { value: "paused", label: "Temporarily unavailable", tone: "neutral" },
  { value: "archived", label: "Archived", tone: "neutral" },
];

// -----------------------------------------------------------------------------
// Normaliser — merges an incoming record with defaults so rendering is safe.
// -----------------------------------------------------------------------------

const DEFAULTS = {
  published: true,
  listing_status: "available",
  recordVerificationStatus: "verified",
  vacancy_available: true,
  vacancies: 0,
  total_bedrooms: 0,
  participant_bedrooms: 0,
  occupied_bedrooms: 0,
  ooa_room: false,
  gender_preference: null,
  preferred_participant_profile: "",
  available_from: null,
  vacancy_last_updated: null,
  support_ratios: [],
  support_model: [],
  sda_category: null,
  accessibility_features: [],
  suitability_tags: [],
  property_type: "house",
  bathrooms: 0,
  living_areas: 0,
  outdoor_area: false,
  secure_yard: false,
  parking: 0,
  garage: false,
  nearby: { transport: "", shops: "", medical: "", community: "" },
  floor_plan_url: null,
  video_url: null,
  virtual_tour_url: null,
  brochure_url: null,
  seo: { title: "", description: "", keywords: [], og_image: null },
  internal: {
    sda_provider: "", property_owner: "", referral_source: "",
    landlord: "", management_notes: "", contact: { name: "", email: "", phone: "" },
  },
};

export function normaliseProperty(p) {
  if (!p) return null;
  const merged = { ...DEFAULTS, ...p };
  merged.nearby = { ...DEFAULTS.nearby, ...(p.nearby || {}) };
  merged.seo = { ...DEFAULTS.seo, ...(p.seo || {}) };
  merged.internal = { ...DEFAULTS.internal, ...(p.internal || {}) };
  merged.id = p.id || p.slug;
  merged.hero_image = p.hero_image || (p.images && p.images[0] && p.images[0].src) || "";
  merged.gallery = p.gallery || p.images || [];
  return merged;
}

/** Public-safe view — strips internal fields for API/UI use. */
export function publicView(p) {
  if (!p) return null;
  const n = normaliseProperty(p);
  // eslint-disable-next-line no-unused-vars
  const { internal, ...safe } = n;
  return safe;
}

/** Match a property against a filter set. All arrays use AND logic between
 *  categories and OR within a category. */
export function matchesFilters(p, f) {
  if (!p || !f) return true;
  if (f.state && p.state !== f.state) return false;
  if (f.city && p.city && p.city !== f.city) return false;
  if (f.suburb && p.suburb && !p.suburb.toLowerCase().includes(f.suburb.toLowerCase())) return false;
  if (f.vacancy_only && !p.vacancy_available) return false;
  if (f.support_ratios && f.support_ratios.length > 0) {
    if (!p.support_ratios || !p.support_ratios.some((r) => f.support_ratios.includes(r))) return false;
  }
  if (f.support_model && f.support_model.length > 0) {
    if (!p.support_model || !p.support_model.some((m) => f.support_model.includes(m))) return false;
  }
  if (f.sda_categories && f.sda_categories.length > 0) {
    if (!p.sda_category || !f.sda_categories.includes(p.sda_category)) return false;
  }
  if (f.accessibility && f.accessibility.length > 0) {
    if (!p.accessibility_features || !p.accessibility_features.some((a) => f.accessibility.includes(a))) return false;
  }
  if (f.suitability && f.suitability.length > 0) {
    if (!p.suitability_tags || !p.suitability_tags.some((s) => f.suitability.includes(s))) return false;
  }
  if (f.property_type && f.property_type.length > 0) {
    if (!p.property_type || !f.property_type.includes(p.property_type)) return false;
  }
  return true;
}

/** Attribution helper — every enquiry should carry these fields for future
 *  CRM / Jotform / DocuSign integrations. */
export function propertyEnquiryContext(p, extra = {}) {
  return {
    property_id: p?.id || p?.slug || null,
    property_name: p?.name || null,
    property_slug: p?.slug || null,
    property_url: p?.href ? `https://alphacommunitycare.com.au${p.href}` : null,
    property_state: p?.state || null,
    property_city: p?.city || null,
    property_suburb: p?.suburb || null,
    listing_status: p?.listing_status || null,
    ...extra,
  };
}

/**
 * Production safeguard — determines whether the current environment should
 * expose demonstration properties.
 *
 * Preview environments (hostname !== canonical) show demo inventory so the
 * search UI can be demonstrated and tested. Production (canonical hostname)
 * hides demo properties completely — only `verified` records surface.
 *
 * `awaitingVerification` records are always hidden from public listings; they
 * live in an internal review queue until confirmed.
 */
export function isPreviewEnvironment() {
  if (typeof window === "undefined") return true; // SSR / build — safe default
  const canonicalHost = "alphacommunitycare.com.au";
  const host = window.location.hostname;
  return !(host === canonicalHost || host === `www.${canonicalHost}`);
}

/** Filter properties by verification tier appropriate to the environment. */
export function filterByVerification(properties) {
  const preview = isPreviewEnvironment();
  return (properties || []).filter((p) => {
    if (!p) return false;
    const s = p.recordVerificationStatus || "verified";
    if (s === "verified") return true;
    if (s === "demonstration") return preview;      // demo only in preview
    if (s === "awaitingVerification") return false; // hidden until approved
    return false;
  });
}
